import { getNextGenerationSchema } from 'Pods/ThreeDStudio/Shared/Utils/getNextGenerationSchema'

import {
  DEFAULT_MODEL_SETTINGS,
  POLY_COUNT,
  DEFAULT_VIEWER,
  DEFAULT_REMESH_SETTINGS,
  DEFAULT_SCHEMA,
  DEFAULT_GENERATION,
  DEFAULT_UI,
  ACTION,
} from '../constants'
import { threeDStudioStore } from '../index'

const setModelSettingsState = (patch) => {
  threeDStudioStore.setState((state) => ({
    modelSettings: { ...state.modelSettings, ...patch },
  }))
}

/**
 * Resets all model settings to their default values.
 */
export const clearModelSettings = () => {
  threeDStudioStore.setState((state) => ({
    modelSettings: { ...DEFAULT_MODEL_SETTINGS },
    viewer: { ...DEFAULT_VIEWER },
    remeshSettings: { ...DEFAULT_REMESH_SETTINGS },
    schema: { ...DEFAULT_SCHEMA },
    ui: {
      ...state.ui,
      openedModel: {},
      isModelSettingsOpen: true,
      isViewerSidebarOpen: false,
      activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab,
    },
  }))
}

/**
 * Sets the prompt
 * @param {string} prompt - User prompt for generation
 */
export const setPrompt = (prompt) => {
  setModelSettingsState({ prompt })
}

/**
 * Sets the polygon count
 * @param {string} polyCount - One of: "low", "medium", "high"
 * @param {boolean} skipSelectedUseCaseUpdate - Whether to skip updating the selected use case
 */
export const setPolyCount = (polyCount, skipSelectedUseCaseUpdate = false) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const { polygonType } = modelSettings
  const generationModelSchema = schema?.generationModelSchema
  const faceCountSchema = generationModelSchema?.input_schema?.properties?.face_count
  const targetPolycountSchema = generationModelSchema?.input_schema?.properties?.target_polycount
  const inputSchema = faceCountSchema || targetPolycountSchema

  if (inputSchema) {
    const { minimum, maximum, default: defaultValue } = (() => {
      const schemaKey = faceCountSchema ? 'face_count' : 'target_polycount'

      if (inputSchema?.oneOf) {
        const matched = inputSchema.oneOf.find(
          entry => entry?.if?.properties?.polygon_type?.const === polygonType,
        )

        if (matched) {
          return matched?.then?.properties?.[schemaKey]
        }
      }

      return inputSchema
    })()
    const targetPolyCount = {
      [POLY_COUNT.LOW]: minimum,
      [POLY_COUNT.MEDIUM]: defaultValue,
      [POLY_COUNT.HIGH]: maximum,
    }[polyCount]

    if (skipSelectedUseCaseUpdate === true) {
      setModelSettingsState({ polyCount, targetPolycount: targetPolyCount })

      return
    }

    setModelSettingsState({
      polyCount,
      targetPolycount: targetPolyCount,
      selectedUseCase: undefined,
    })

    return
  }

  setModelSettingsState({ polyCount, selectedUseCase: undefined })
}

/**
 * Sets the generate type
 * @param {string} generateType - One of: "normal", "lowPoly" or undefined
 */
export const setGenerateType = (generateType) => {
  setModelSettingsState({ generateType })
}

/**
 * Sets the material type
 * @param {string} material - One of: "pbr", "basic", "geometry_only"
 */
export const setMaterial = (material) => {
  setModelSettingsState({ material, selectedUseCase: undefined })
}

/**
 * Sets the polygon type
 * @param {string} polygonType - One of: "quadrilateral", "triangle" or undefined
 */
export const setPolygonType = (polygonType) => {
  setModelSettingsState({ polygonType, selectedUseCase: undefined })
}

/**
 * Sets the target polygon count
 * @param {number|undefined} targetPolycount - Number or empty string
 */
export const setTargetPolycount = (targetPolycount) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const { polygonType } = modelSettings
  const generationModelSchema = schema?.generationModelSchema
  const faceCountSchema = generationModelSchema?.input_schema?.properties?.face_count
  const targetPolycountSchema = generationModelSchema?.input_schema?.properties?.target_polycount
  const inputSchema = faceCountSchema || targetPolycountSchema

  let polyCount = modelSettings.polyCount
  if (inputSchema && targetPolycount !== '' && targetPolycount != null) {
    const schemaKey = faceCountSchema ? 'face_count' : 'target_polycount'
    const range = (() => {
      if (inputSchema?.oneOf) {
        const matched = inputSchema.oneOf.find(
          entry => entry?.if?.properties?.polygon_type?.const === polygonType,
        )

        if (matched) {
          return matched?.then?.properties?.[schemaKey]
        }
      }

      return inputSchema
    })()
    const { minimum, maximum, default: defaultValue } = range || {}
    const numericValue = Number(targetPolycount)
    if (numericValue === minimum) {
      polyCount = POLY_COUNT.LOW
    } else if (numericValue === maximum) {
      polyCount = POLY_COUNT.HIGH
    } else if (numericValue === defaultValue) {
      polyCount = POLY_COUNT.MEDIUM
    } else {
      polyCount = undefined
    }
  }

  setModelSettingsState({ targetPolycount, polyCount, selectedUseCase: undefined })
}

/**
 * Sets the input image (asset ID)
 * @param {number|undefined} id - Input image asset id
 * @param {string|undefined} url - Input image URL (e.g. blob URL or file URL)
 */
export const setInputImage = (id, url) => {
  if (id === undefined) {
    threeDStudioStore.setState((state) => ({
      modelSettings: {
        ...state.modelSettings,
        inputImage: undefined,
        inputImageUrl: undefined,
      },
    }))

    return
  }

  threeDStudioStore.setState((state) => ({
    modelSettings: {
      ...state.modelSettings,
      inputImage: id,
      inputImageUrl: url,
    },
    schema: {
      ...state.schema,
      generationAction: ACTION.IMAGE_TO_3D,
    },
    generation: {
      ...state.generation,
      sessionId: undefined,
      runId: undefined,
    },
  }))
}

/**
 * Sets the input threeD (asset ID)
 * @param {number|undefined} id - Input threeD asset id
 * @param {string|undefined} url - Input threeD URL (e.g. blob URL or file URL)
 * @param {number|undefined} size - Input threeD size in bytes
 * @param {string|undefined} format - Input threeD format (e.g. "glb", "fbx", "obj", "stl")
 */
export const setInputThreeD = (id, url, size, format) => {
  threeDStudioStore.setState((state) => ({
    modelSettings: {
      ...state.modelSettings,
      inputThreeDUrl: url,
    },
    generation: { ...DEFAULT_GENERATION },
    viewer: {
      ...state.viewer,
      inputThreeD: id,
      fileFormat: format,
      fileSize: size,
    },
  }))
}

/**
 * Sets the input threeD URL
 * @param {string|undefined} inputThreeDUrl - Input threeD URL (e.g. blob URL or file URL)
 */
export const setInputThreeDUrl = (inputThreeDUrl) => {
  setModelSettingsState({ inputThreeDUrl })
}

/**
 * Sets the input threeD format (file extension) for blob URLs
 * @param {string|undefined} inputThreeDFormat - One of: "glb", "fbx", "obj", "stl"
 */
export const setInputThreeDFormat = (inputThreeDFormat) => {
  setModelSettingsState({ inputThreeDFormat })
}

/**
 * Sets the private flag
 * @param {boolean} isPrivate - Whether the content should be private
 */
export const setIsPrivate = (isPrivate) => {
  setModelSettingsState({ isPrivate })
}

/**
 * Sets the smart low poly flag
 * @param {boolean} isSmartLowPoly - Whether smart low poly is enabled
 */
export const setIsSmartLowPoly = (isSmartLowPoly) => {
  setModelSettingsState({ isSmartLowPoly, selectedUseCase: undefined })
}

/**
 * Applies a use case preset to the store
 * @param {Object} useCase - The use case to apply, with `id` and `preset`
 */
export const applyUseCasePreset = (id, preset) => {
  const {
    polyCount,
    polygonType,
    material,
    isSmartLowPoly,
    targetPolycount: presetTargetPolycount,
  } = preset

  const generationAction = threeDStudioStore.getState().schema.generationAction
  const modelSettings = threeDStudioStore.getState().modelSettings
  const modelSettingsPatch = {
    ...modelSettings,
    ...(polyCount !== undefined && { polyCount }),
    ...(polygonType !== undefined && { polygonType }),
    ...(material !== undefined && { material }),
    ...(presetTargetPolycount !== undefined && { targetPolycount: presetTargetPolycount }),
    isSmartLowPoly,
    selectedUseCase: id,
  }
  const generationModelSchema = getNextGenerationSchema(generationAction, modelSettingsPatch)

  if (presetTargetPolycount === undefined && polyCount !== undefined) {
    const faceCountSchema = generationModelSchema?.input_schema?.properties?.face_count
    const targetPolycountSchema = generationModelSchema?.input_schema?.properties?.target_polycount
    const inputSchemaField = faceCountSchema || targetPolycountSchema
    if (inputSchemaField) {
      const schemaKey = faceCountSchema ? 'face_count' : 'target_polycount'
      const resolvedSchema = (() => {
        if (inputSchemaField?.oneOf) {
          const matched = inputSchemaField.oneOf.find(
            entry => entry?.if?.properties?.polygon_type?.const === modelSettingsPatch.polygonType,
          )
          if (matched) {
            return matched?.then?.properties?.[schemaKey]
          }
        }
        return inputSchemaField
      })()
      const { minimum, maximum, default: defaultValue } = resolvedSchema || {}
      const targetPolyCount = {
        [POLY_COUNT.LOW]: minimum,
        [POLY_COUNT.MEDIUM]: defaultValue,
        [POLY_COUNT.HIGH]: maximum,
      }[polyCount]

      modelSettingsPatch.targetPolycount = targetPolyCount
    }
  }

  threeDStudioStore.setState((state) => ({
    schema: {
      ...state.schema,
      ...(generationModelSchema && { generationModelSchema }),
    },
    modelSettings: { ...state.modelSettings, ...modelSettingsPatch },
  }))
}
